// Classical ciphers on the Latin alphabet. Letter case and every non-letter (digits,
// spaces, punctuation, accented letters) pass through unchanged, which is how these
// ciphers are taught and how puzzle answers expect them.

const A = 65;
const a = 97;

function shiftChar(ch: string, shift: number): string {
  const code = ch.charCodeAt(0);
  const base = code >= A && code <= A + 25 ? A : code >= a && code <= a + 25 ? a : -1;
  if (base < 0) return ch;
  return String.fromCharCode(base + ((((code - base + shift) % 26) + 26) % 26));
}

export function caesar(text: string, shift: number): string {
  return [...text].map((ch) => shiftChar(ch, shift)).join('');
}

export function rot13(text: string): string {
  return caesar(text, 13);
}

export function atbash(text: string): string {
  return [...text]
    .map((ch) => {
      const code = ch.charCodeAt(0);
      if (code >= A && code <= A + 25) return String.fromCharCode(A + 25 - (code - A));
      if (code >= a && code <= a + 25) return String.fromCharCode(a + 25 - (code - a));
      return ch;
    })
    .join('');
}

/** Vigenère: the key advances only on letters, so spaces do not shift the key. */
export function vigenere(text: string, key: string, decode = false): string {
  const shifts = [...key.toLowerCase()]
    .filter((c) => c >= 'a' && c <= 'z')
    .map((c) => c.charCodeAt(0) - a);
  if (!shifts.length) return text;
  let i = 0;
  return [...text]
    .map((ch) => {
      if (!/[A-Za-z]/.test(ch)) return ch;
      const s = shifts[i % shifts.length];
      i += 1;
      return shiftChar(ch, decode ? -s : s);
    })
    .join('');
}

// English letter frequencies (%), for ranking Caesar brute-force candidates.
const FREQ: Record<string, number> = {
  e: 12.7,
  t: 9.1,
  a: 8.2,
  o: 7.5,
  i: 7.0,
  n: 6.7,
  s: 6.3,
  h: 6.1,
  r: 6.0,
  d: 4.3,
  l: 4.0,
  c: 2.8,
  u: 2.8,
  m: 2.4,
  w: 2.4,
  f: 2.2,
  g: 2.0,
  y: 2.0,
  p: 1.9,
  b: 1.5,
  v: 1.0,
  k: 0.8,
  j: 0.15,
  x: 0.15,
  q: 0.1,
  z: 0.07,
};

// Common English words: a short message has too few letters for frequencies alone, so a
// recognised word pulls its shift to the top.
const COMMON = new Set(
  'the be to of and a in that have i it for not on with he as you do at this but his by from they we say her she or an will my one all would there their what so up out if about who get which go me when make can like time no just him know take people into year your good some could them see other than then now look only come its over think also back after use two how our work first well way even new want because any these give day most us is are was were has had hello hi yes help attack dawn secret message meet'.split(
    ' ',
  ),
);

/** Every Caesar shift of `text`, most English-looking first (chi-squared on letter counts). */
export function caesarCandidates(
  text: string,
): Array<{ shift: number; text: string; score: number }> {
  const out = [];
  for (let shift = 1; shift < 26; shift += 1) {
    const plain = caesar(text, -shift);
    const letters = plain.toLowerCase().replace(/[^a-z]/g, '');
    let score = 0;
    if (letters.length) {
      const counts: Record<string, number> = {};
      for (const c of letters) counts[c] = (counts[c] ?? 0) + 1;
      for (const [c, pct] of Object.entries(FREQ)) {
        const expected = (pct / 100) * letters.length;
        score += ((counts[c] ?? 0) - expected) ** 2 / expected;
      }
    }
    const words = plain.toLowerCase().match(/[a-z]+/g) ?? [];
    const known = words.filter((w) => COMMON.has(w)).length;
    if (words.length) score *= 1 - 0.8 * (known / words.length);
    out.push({ shift, text: plain, score });
  }
  return out.sort((x, y) => x.score - y.score);
}
